import { useState } from "react";
import { YStack, XStack, Text, View } from "tamagui";
import { RiMapPinLine } from "react-icons/ri";
import RadiusCard from "@components/layout/RadiusCard";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import KioskAddressSheet from "@screen/Kiosk/components/KioskAddressSheet";
import type { AddAddressFormData } from "@components/address/AddAddressFlowSheet";
import { combinedAddress } from "@components/address/func/addressTool";
import { colors } from "@styles/colors";
import "@screen/Kiosk/kiosk.scss"; // Kiosk Sheet 的 slide-in 動畫 class
import StepLayout from "../StepLayout";
import { PrimaryBtn } from "../Buttons";
import type { AddressData } from "../types";

interface AddressStepProps {
  onNext: (address: AddressData) => void;
  onBack: () => void;
}

/**
 * 步驟 2：填寫配送地址。進頁自動彈出「新增送達地址」Sheet（沿用點餐機的
 * KioskAddressSheet：縣市/區/路/門牌/樓/室 + MapPicker 地圖微調 + GPS/geocode）。
 */
const AddressStep = ({ onNext, onBack }: AddressStepProps) => {
  const [address, setAddress] = useState<AddressData | null>(null);
  const [sheetOpen, setSheetOpen] = useState(true); // 進頁即彈出

  const handleSubmit = (data: AddAddressFormData) => {
    setAddress({
      address: combinedAddress(data),
      city: data.city,
      area: data.area,
      road: data.road,
      streetNumber: data.streetNumber,
      floor: data.floor,
      room: data.room,
      otherAddress: data.otherAddress,
      latitude: data.latitude ?? null,
      longitude: data.longitude ?? null,
    });
    setSheetOpen(false);
  };

  return (
    <YStack position="relative" height="100dvh" width="100vw">
      <StepLayout
        title="您的地址"
        subtitle="我們會幫您找附近的瓦斯行"
        onBack={onBack}
        step={2}
        footer={
          <PrimaryBtn active={!!address} onPress={() => address && onNext(address)}>
            下一步
          </PrimaryBtn>
        }>
        <YStack gap={15}>
          <RadiusCard>
            <RadiusBoxLabel label="配送地址" icon={<RiMapPinLine color={colors.primary} />} />
            <YStack mt="$3">
              {address ? (
                <YStack borderWidth={1.5} borderColor="#BFDBFE" background={colors.primaryBg} borderRadius={12} p="$3.5" gap="$2">
                  <XStack gap="$2.5" ai="flex-start">
                    <View width={32} height={32} borderRadius={8} background="#DBEAFE" ai="center" jc="center">
                      <RiMapPinLine size={16} color={colors.primary} />
                    </View>
                    <YStack flex={1}>
                      <Text fontSize={10} color={colors.sub} fontWeight="600" mb="$1">
                        配送地址
                      </Text>
                      <Text fontSize={14} color={colors.grayDark} fontWeight="600" lineHeight={20}>
                        {address.address}
                      </Text>
                    </YStack>
                  </XStack>
                  <Text color={colors.primary} fontSize={12} fontWeight="600" cursor="pointer" onPress={() => setSheetOpen(true)}>
                    變更地址
                  </Text>
                </YStack>
              ) : (
                <XStack
                  borderWidth={1.5}
                  borderColor="#BFDBFE"
                  borderStyle="dashed"
                  background="#F8FAFF"
                  borderRadius={12}
                  p="$4"
                  ai="center"
                  jc="center"
                  gap="$2"
                  cursor="pointer"
                  onPress={() => setSheetOpen(true)}>
                  <RiMapPinLine size={18} color={colors.primary} />
                  <Text fontSize={14} color={colors.primary} fontWeight="600">
                    選擇您的配送地址
                  </Text>
                </XStack>
              )}
            </YStack>
          </RadiusCard>

          <RadiusCard bg="rgba(255, 255, 255, 0.3)" border="#e2e8f0" borderRadius={10}>
            <XStack gap="$2" ai="center">
              <Text fontSize={16}>📍</Text>
              <Text fontSize={13} color={colors.gray} flex={1} lineHeight={20}>
                地址用於找出附近的合作瓦斯行,日後可隨時修改或新增
              </Text>
            </XStack>
          </RadiusCard>
        </YStack>
      </StepLayout>

      {/* 點餐機式新增地址 Sheet（絕對定位覆蓋整頁） */}
      <KioskAddressSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} onSubmit={handleSubmit} />
    </YStack>
  );
};

export default AddressStep;
